import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ClerkAuthService } from './clerk-auth.service';
import { ClerkAuthStore } from './clerk-auth.store';
import {
  buildApiUrl,
  getApiErrorMessage,
  getApiResponseMessage
} from './auth.utils';
import { IAuthFacadeState, IAuthProfile, IAuthProviderConfig, ProfileHydrationStatus } from '../models/auth.models';
import { IApiResponse } from '@scl/core/shared/models/api-response';

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private providerConfigPromise: Promise<IAuthProviderConfig> | null = null;
  private loadProfilePromise: Promise<boolean> | null = null;

  private readonly clerkAuthService = inject(ClerkAuthService);
  private readonly clerkAuthStore = inject(ClerkAuthStore);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly state = signal<IAuthFacadeState>({
    providerConfig: null,
    status: 'idle',
    message: null,
    user: null,
    profileStatus: 'not-loaded'
  });

  readonly providerConfig = computed(() => this.state().providerConfig);
  readonly isProviderReady = computed(() => this.state().providerConfig !== null);
  readonly isClerk = computed(() => this.state().providerConfig?.provider === 'Clerk');
  readonly user = computed(() => this.state().user);
  readonly profileStatus = computed(() => this.state().profileStatus);
  readonly clerkUser = this.clerkAuthStore.clerkUser;
  readonly isBusy = computed(() => this.state().status === 'loading');
  readonly hasError = computed(() => this.state().status === 'error');
  readonly isAuthenticated = computed(() => this.clerkAuthStore.isAuthenticated());
  readonly isProfileLoaded = computed(() => this.profileStatus() === 'loaded');
  readonly isImpersonating = computed(() => this.clerkAuthStore.isImpersonating());
  readonly hasReturnTicket = computed(() => this.clerkAuthStore.hasReturnTicket());

  async impersonate(ticket: string): Promise<void> {
    await this.clerkAuthStore.impersonate(ticket);
  }

  async returnFromImpersonation(): Promise<boolean> {
    const restored = await this.clerkAuthStore.returnFromImpersonation();
    if (!restored) {
      this.clearSession();
      this.patchState({ message: 'Your admin session has expired. Please sign in again.' });
      await this.router.navigateByUrl('/login');
    }
    return restored;
  }

  readonly isProfileDegraded = computed(() => this.profileStatus() === 'unavailable');
  readonly statusMessage = computed(() => this.state().message ?? this.clerkAuthService.errorMessage());

  /**
   * This method stores the Promise reference in the class field. This is the deduplication mechanism:
   * Caller A: ensureProviderConfig() → providerConfigPromise is null → assigns Promise → returns Promise
   * Caller B: ensureProviderConfig() → providerConfigPromise is NOT null → returns SAME Promise
   *                                        ↑ reuses the in-flight request
   * HTTP completes once → both callers resolve with the same config
   * Inside the method, we're just managing the Promise reference for sharing.
   * 
   * Returns Promise (not Observable) for three reasons:
   * - Deduplication: caches a single Promise so concurrent callers share one HTTP call.
   * - Early return: when config is already loaded, exits synchronously — no subscription setup.
   * - All callers are async methods that need the config before proceeding (`await`), so
   *   they'd call `firstValueFrom()` anyway.
   * 
   * Explanation:
   * 1. Multiple callers share one in-flight request. During app startup, restoreSession(), saveClerkSignUpStepTwo(), doLoadProfile(), and getAuthorizationToken() may all call ensureProviderConfig() concurrently. By caching a single Promise, only one HTTP call fires — all callers await the same instance. With Observable you'd need shareReplay(1) + a Subject + teardown logic to achieve the same.
   * 2. Early return for the cached case. When the config is already loaded, return providerConfig exits immediately — no subscription setup, no pipe chain. Promises handle synchronous-or-async branching naturally.
   */
  async ensureProviderConfig(): Promise<IAuthProviderConfig> {
    // 1. Fast path — already loaded, return synchronously
    const providerConfig = this.state().providerConfig;
    if (providerConfig) {
      return providerConfig;
    }

    // 2. Deduplication — reuse the same in-flight Promise if the config is currently being loaded by another caller
    if (!this.providerConfigPromise) {
      this.providerConfigPromise = this.loadProviderConfig()
        .finally(() => {
          if (!this.state().providerConfig) {
            this.providerConfigPromise = null;
          }
        });
    }

    // 3. All callers await the SAME Promise instance
    return this.providerConfigPromise;
  }

  async restoreSession(): Promise<boolean> {
    try {
      await this.ensureProviderConfig();

      const hasSession = await this.ensureClerkSessionHydrated();
      if (!hasSession) {
        return false;
      }

      // if (this.state().profileStatus === 'not-loaded') {
      //   void this.loadProfile();
      // }

      return true;
    } catch (error) {
      this.patchState({ status: 'error', message: getApiErrorMessage(error) });
      return false;
    }
  }

  // async loadProfile(): Promise<boolean> {
  //   if (!this.loadProfilePromise) {
  //     this.loadProfilePromise = this.doLoadProfile()
  //       .finally(() => {
  //         this.loadProfilePromise = null;
  //       });
  //   }

  //   return this.loadProfilePromise;
  // }

  // async refreshSession(): Promise<boolean> {
  //   this.state.update((current) => ({
  //     ...current,
  //     message: null,
  //     profileStatus: 'not-loaded'
  //   }));

  //   return this.loadProfile();
  // }

  async logout(): Promise<void> {
    await this.clerkAuthStore.logout();

    this.clearSession();
    this.patchState({ message: 'You have been signed out.' });
    await this.router.navigateByUrl('/login');
  }

  async handleClerkAuthenticationFailure(message = 'Your session expired. Please sign in again.'): Promise<void> {
    this.clearSession();
    this.patchState({ status: 'error', message });
    return this.clerkAuthStore.handleClerkAuthenticationFailure(message);
  }

  async getAuthorizationToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
    const providerConfig = this.state().providerConfig ?? await this.ensureProviderConfig();

    if (providerConfig.provider !== 'Clerk') {
      return null;
    }

    return this.clerkAuthStore.getAuthorizationToken(options);
  }

  clearStatusMessage(): void {
    this.patchState({ message: null, status: 'idle' });
  }

  private async doLoadProfile(): Promise<boolean> {
    try {
      const providerConfig = this.state().providerConfig ?? await this.ensureProviderConfig();
      if (providerConfig.provider !== 'Clerk') {
        return false;
      }

      const hasSession = await this.ensureClerkSessionHydrated();
      if (!hasSession) {
        return false;
      }

      this.patchState({
        status: 'loading',
        message: null,
        profileStatus: 'loading'
      });

      const response = await firstValueFrom(
        this.http.get<IApiResponse<IAuthProfile>>(buildApiUrl('/api/profile/me'))
      );

      const profile = response.result;
      const hasClerkProfile = Boolean(profile?.clerkUser);
      if (!response.success || !profile || !hasClerkProfile) {
        return this.markProfileStatus(
          'unavailable',
          getApiResponseMessage(response, 'Unable to load the current profile.')
        );
      }

      this.clerkAuthStore.setClerkUser(profile.clerkUser ?? null);

      const localProfileStatus = profile.localProfileStatus ?? (profile.localUser ? 'resolved' : 'missing');
      const nextProfileStatus: ProfileHydrationStatus = profile.localUser
        ? 'loaded'
        : localProfileStatus === 'degraded' ? 'unavailable' : 'onboarding-required';

      this.state.update((current) => ({
        ...current,
        status: 'idle',
        message: nextProfileStatus === 'loaded' ? null : response.message ?? 'Profile ready.',
        user: profile.localUser ?? null,
        profileStatus: nextProfileStatus
      }));
      return true;
    } catch (error) {
      if (this.isAuthenticationFailure(error)) {
        this.patchState({
          status: 'error',
          message: getApiErrorMessage(error),
          profileStatus: 'not-loaded'
        });
        return false;
      }

      return this.markProfileStatus('unavailable', getApiErrorMessage(error));
    }
  }

  private async loadProviderConfig(): Promise<IAuthProviderConfig> {
    try {
      const response = await firstValueFrom(
        this.http.get<IApiResponse<IAuthProviderConfig>>(buildApiUrl('/api/auth/provider'))
      );

      console.log('Loaded provider config: ', response);

      if (!response.success || !response.result) {
        throw new Error(getApiResponseMessage(response, 'Unable to determine the current authentication provider.'));
      }

      this.applyProviderConfig(response.result);
      return response.result;
    } catch (error) {
      this.patchState({ status: 'error', message: getApiErrorMessage(error) });
      throw error;
    }
  }

  private applyProviderConfig(providerConfig: IAuthProviderConfig): void {
    this.clerkAuthService.configure(
      providerConfig.clerkPublishableKey,
      providerConfig.authorizedParties
    );

    this.state.update((current) => ({
      ...current,
      providerConfig
    }));
  }

  private rejectUnsupportedClerkSignUpStepTwoAction(): boolean {
    return this.rejectUnsupportedAction(
      'Clerk sign-up details can be saved only while Clerk authentication is active.'
    );
  }

  private rejectUnsupportedAction(message: string): boolean {
    this.patchState({
      status: 'error',
      message
    });

    return false;
  }

  private async ensureClerkSessionHydrated(): Promise<boolean> {
    if (this.clerkAuthStore.isAuthenticated()) {
      return true;
    }

    const restored = await this.clerkAuthStore.restoreSession();
    if (!restored) {
      this.clearSession();
      return false;
    }

    const hydrated = await this.clerkAuthStore.hydrateFromClerkSdk();
    if (!hydrated) {
      this.clearSession();
      return false;
    }

    return true;
  }

  private markProfileStatus(profileStatus: ProfileHydrationStatus, message: string): boolean {
    this.state.update((current) => ({
      ...current,
      status: 'idle',
      message,
      profileStatus
    }));

    return true;
  }

  private isAuthenticationFailure(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 401;
  }

  private clearSession(): void {
    this.clerkAuthStore.clearSession();

    this.state.update((current) => ({
      ...current,
      status: 'idle',
      message: null,
      user: null,
      profileStatus: 'not-loaded'
    }));
  }

  private patchState(patch: Partial<IAuthFacadeState>): void {
    this.state.update((current) => ({ ...current, ...patch }));
  }
}
